import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Brain, Activity, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  RefreshCw, Shield, Target, Zap, Clock, BarChart3, PieChart, FileText,
  Send, Loader2, AlertCircle, ChevronRight, Eye, ArrowUpRight, ArrowDownRight,
  Minus, Database, Server, HardDrive, Cpu, Settings, Mail, Calendar, Download,
  DollarSign, Users, Wallet
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { AIExportDropdown } from '../components/AIExportDropdown';
import { InsightsExport } from '../components/InsightsExport';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '../components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk classification colors
const RISK_COLORS = {
  STABLE: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  WARNING: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  HIGH_RISK: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  CRITICAL: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  NO_DATA: { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-300' }
};

const TREND_ICONS = {
  up: <TrendingUp className="h-4 w-4 text-emerald-500" />,
  down: <TrendingDown className="h-4 w-4 text-red-500" />,
  stable: <Minus className="h-4 w-4 text-slate-400" />,
  insufficient_data: <AlertCircle className="h-4 w-4 text-slate-400" />
};

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' }
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily', description: 'Run every day' },
  { value: 'weekly', label: 'Weekly', description: 'Run once a week' },
  { value: 'monthly', label: 'Monthly', description: 'Run once a month' }
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`
}));

export default function AIIntelligencePage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [diagnostics, setDiagnostics] = useState(null);
  const [weeklyAnalysis, setWeeklyAnalysis] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [riskIntel, setRiskIntel] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [executiveBrief, setExecutiveBrief] = useState(null);
  const [financialInsights, setFinancialInsights] = useState(null);
  const [crossModuleSummary, setCrossModuleSummary] = useState(null);
  
  // Schedule settings
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 'mon',
    day_of_month: 1,
    hour: 7,
    minute: 0,
    recipients: []
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  
  // Loading states
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);

  useEffect(() => {
    fetchAllData();
    fetchScheduleConfig();
  }, [token]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchWeeklyAnalysis(),
      fetchPredictions(),
      fetchRiskIntel(),
      fetchSystemStatus(),
      fetchFinancialInsights(),
      fetchCrossModuleSummary()
    ]);
    setLoading(false);
  };

  const fetchCrossModuleSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/cross-module-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCrossModuleSummary(data);
      }
    } catch (error) {
      console.error('Error fetching cross-module summary:', error);
    }
  };

  const fetchScheduleConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScheduleConfig(data);
        setRecipientInput(data.recipients?.join(', ') || '');
      }
    } catch (error) {
      console.error('Error fetching schedule config:', error);
    }
  };

  const saveScheduleConfig = async () => {
    setSavingSchedule(true);
    try {
      const recipients = recipientInput.split(',').map(e => e.trim()).filter(e => e);
      const params = new URLSearchParams({
        enabled: scheduleConfig.enabled,
        frequency: scheduleConfig.frequency,
        day_of_week: scheduleConfig.day_of_week,
        day_of_month: scheduleConfig.day_of_month,
        hour: scheduleConfig.hour,
        minute: scheduleConfig.minute
      });
      
      // Add recipients as repeated query params (FastAPI List[str] format)
      recipients.forEach(email => params.append('recipients', email));
      
      const res = await fetch(`${API_URL}/api/ai-intelligence-schedule?${params}`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.next_run) {
          toast.info(`Next report: ${new Date(data.next_run).toLocaleString()}`);
        }
        fetchScheduleConfig();
      } else {
        toast.error('Failed to save schedule');
      }
    } catch (error) {
      toast.error('Error saving schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const runReportNow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence-report/run-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('AI Intelligence report sent!');
      } else {
        toast.error('Failed to send report');
      }
    } catch (error) {
      toast.error('Error sending report');
    }
  };

  const fetchWeeklyAnalysis = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/weekly-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWeeklyAnalysis(data);
      }
    } catch (error) {
      console.error('Error fetching weekly analysis:', error);
    }
  };

  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/predictions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPredictions(data);
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const fetchRiskIntel = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/risk-intelligence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRiskIntel(data);
      }
    } catch (error) {
      console.error('Error fetching risk intelligence:', error);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/system-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSystemStatus(data);
      }
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  };

  const fetchFinancialInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/monthly-financials/ai-insights/quick`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFinancialInsights(data);
      }
    } catch (error) {
      console.error('Error fetching financial insights:', error);
    }
  };

  const runDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDiagnostics(data);
        toast.success('Diagnostics completed');
      } else {
        toast.error('Failed to run diagnostics');
      }
    } catch (error) {
      toast.error('Error running diagnostics');
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const generateAIAnalysis = async () => {
    setLoadingAI(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/ai-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data);
        toast.success('AI analysis generated');
      } else {
        toast.error('Failed to generate AI analysis');
      }
    } catch (error) {
      toast.error('Error generating AI analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  const generateExecutiveBrief = async (sendEmail = false) => {
    setGeneratingBrief(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-intelligence/generate-executive-brief?send_email=${sendEmail}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExecutiveBrief(data);
        toast.success(sendEmail ? 'Executive brief generated and email queued' : 'Executive brief generated');
      } else {
        toast.error('Failed to generate executive brief');
      }
    } catch (error) {
      toast.error('Error generating executive brief');
    } finally {
      setGeneratingBrief(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
        <span className="ml-3 text-lg">Loading AI Intelligence...</span>
      </div>
    );
  }

  const summary = weeklyAnalysis?.summary || { total_kpis: 0, stable: 0, warning: 0, high_risk: 0, critical: 0 };

  return (
    <div className="space-y-6" data-testid="ai-intelligence-page">
      {/* Modern Header */}
      <PageHeader
        icon={Brain}
        title="AI Performance Intelligence"
        subtitle="Comprehensive KPI tracking, predictive analytics, and risk intelligence"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={fetchAllData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <AIExportDropdown token={token} />
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => generateExecutiveBrief(false)} disabled={generatingBrief}>
            {generatingBrief ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Brief
          </Button>
          <Button onClick={() => generateExecutiveBrief(true)} disabled={generatingBrief} className="bg-white text-[#00b0ab] hover:bg-white/90">
            {generatingBrief ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Brief + Email
          </Button>
        </div>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard title="Total KPIs" value={summary.total_kpis} subtitle="Tracked metrics" icon={BarChart3} gradient="slate" />
        <MetricCard title="Stable" value={summary.stable} subtitle="On target" icon={CheckCircle2} gradient="emerald" />
        <MetricCard title="Warning" value={summary.warning} subtitle="Needs attention" icon={Eye} gradient="amber" />
        <MetricCard title="High Risk" value={summary.high_risk} subtitle="Review needed" icon={AlertTriangle} gradient="orange" />
        <MetricCard title="Critical" value={summary.critical} subtitle="Immediate action" icon={AlertCircle} gradient="rose" />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full max-w-5xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="kpis">KPI Analysis</TabsTrigger>
          <TabsTrigger value="predictions">Predictions</TabsTrigger>
          <TabsTrigger value="risks">Risk Intel</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
          <TabsTrigger value="ai">AI Analysis</TabsTrigger>
          <TabsTrigger value="schedule" className="gap-1">
            <Settings className="h-3 w-3" />
            Schedule
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Executive Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-[#00b0ab]" />
                  Executive Summary
                </CardTitle>
                <CardDescription>What changed this week</CardDescription>
              </CardHeader>
              <CardContent>
                {riskIntel?.executive_summary?.what_changed_this_week?.length > 0 ? (
                  <ul className="space-y-2">
                    {riskIntel.executive_summary.what_changed_this_week.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <ChevronRight className="h-4 w-4 mt-0.5 text-[#00b0ab]" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">No significant changes detected</p>
                )}
                
                {riskIntel?.executive_summary?.recommended_interventions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-2">Recommended Actions:</h4>
                    <ul className="space-y-1">
                      {riskIntel.executive_summary.recommended_interventions.slice(0, 3).map((item, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <Target className="h-3 w-3 mt-1 text-amber-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Concerns */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Top Concerns
                </CardTitle>
                <CardDescription>KPIs requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyAnalysis?.top_concerns?.length > 0 ? (
                  <div className="space-y-3">
                    {weeklyAnalysis.top_concerns.slice(0, 5).map((concern, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${RISK_COLORS[concern.risk]?.bg} ${RISK_COLORS[concern.risk]?.border}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{concern.kpi}</span>
                          <Badge className={RISK_COLORS[concern.risk]?.bg + ' ' + RISK_COLORS[concern.risk]?.text}>
                            {concern.risk}
                          </Badge>
                        </div>
                        {concern.value && (
                          <p className="text-sm text-muted-foreground mt-1">Current: {concern.value}%</p>
                        )}
                        {concern.flags?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">{concern.flags[0]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No critical concerns identified</p>
                )}
              </CardContent>
            </Card>

            {/* 4-Week Predictions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#00b0ab]" />
                  4-Week Forecast Alerts
                </CardTitle>
                <CardDescription>Predictive intelligence</CardDescription>
              </CardHeader>
              <CardContent>
                {predictions?.executive_alerts?.length > 0 ? (
                  <ul className="space-y-2">
                    {predictions.executive_alerts.slice(0, 5).map((alert, idx) => (
                      <li key={idx} className="text-sm p-2 bg-slate-50 rounded-lg">
                        {alert}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No forecast alerts</p>
                )}
              </CardContent>
            </Card>

            {/* System Health */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-slate-500" />
                  System Health
                </CardTitle>
                <CardDescription>Data integrity status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-3 w-3 rounded-full ${
                    systemStatus?.overall_health === 'HEALTHY' ? 'bg-emerald-500' :
                    systemStatus?.overall_health === 'WARNING' ? 'bg-amber-500' : 'bg-red-500'
                  }`} />
                  <span className="font-medium">{systemStatus?.overall_health || 'Unknown'}</span>
                </div>
                
                {systemStatus?.alerts?.length > 0 ? (
                  <ul className="space-y-2">
                    {systemStatus.alerts.slice(0, 3).map((alert, idx) => (
                      <li key={idx} className="text-sm p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <span className="font-medium">{alert.type}</span>: {alert.message}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-emerald-600">All systems operational</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cross-Module Intelligence Summary */}
          {crossModuleSummary && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-[#00b0ab]" />
                  Cross-Module Intelligence
                </CardTitle>
                <CardDescription>Real-time data consistency across all system domains</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Executive Health Score */}
                <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-[#00b0ab]/20 dark:to-[#008a86]/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Executive Health Score</p>
                      <p className="text-3xl font-bold text-[#00b0ab] dark:text-[#33c3bf]">{crossModuleSummary.executive_health_score}%</p>
                    </div>
                    <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
                      crossModuleSummary.executive_health_score >= 80 ? 'bg-emerald-100 text-emerald-600' :
                      crossModuleSummary.executive_health_score >= 60 ? 'bg-amber-100 text-amber-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {crossModuleSummary.executive_health_score >= 80 ? 
                        <CheckCircle2 className="h-8 w-8" /> :
                        crossModuleSummary.executive_health_score >= 60 ?
                        <AlertCircle className="h-8 w-8" /> :
                        <AlertTriangle className="h-8 w-8" />
                      }
                    </div>
                  </div>
                </div>

                {/* Domain Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                  {/* Census Domain */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">ADC</p>
                    <p className="text-xl font-bold">{crossModuleSummary.domains?.census?.adc || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      +{crossModuleSummary.domains?.census?.weekly_admissions || 0} / -{crossModuleSummary.domains?.census?.weekly_discharges || 0}
                    </p>
                  </div>

                  {/* Clinical Domain */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Pain Control</p>
                    <p className={`text-xl font-bold ${
                      (crossModuleSummary.domains?.clinical?.pain_control || 0) >= 90 ? 'text-emerald-600' :
                      (crossModuleSummary.domains?.clinical?.pain_control || 0) >= 80 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>{crossModuleSummary.domains?.clinical?.pain_control?.toFixed(1) || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">Target: 90%</p>
                  </div>

                  {/* Compliance Domain */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Compliance</p>
                    <p className={`text-xl font-bold ${
                      (crossModuleSummary.domains?.compliance?.overall_compliance || 0) >= 95 ? 'text-emerald-600' :
                      (crossModuleSummary.domains?.compliance?.overall_compliance || 0) >= 90 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>{crossModuleSummary.domains?.compliance?.overall_compliance?.toFixed(1) || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">Target: 95%</p>
                  </div>

                  {/* Workforce Domain */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Staff Utilization</p>
                    <p className={`text-xl font-bold ${
                      (crossModuleSummary.domains?.workforce?.staff_utilization || 0) <= 90 ? 'text-emerald-600' :
                      (crossModuleSummary.domains?.workforce?.staff_utilization || 0) <= 100 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>{crossModuleSummary.domains?.workforce?.staff_utilization || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {crossModuleSummary.domains?.workforce?.over_capacity_count || 0} over capacity
                    </p>
                  </div>

                  {/* Financial Domain */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Revenue</p>
                    <p className="text-xl font-bold text-emerald-600">
                      ${(crossModuleSummary.domains?.financial?.revenue / 1000 || 0).toFixed(0)}K
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Margin: {crossModuleSummary.domains?.financial?.net_margin?.toFixed(1) || 0}%
                    </p>
                  </div>
                </div>

                {/* AI Insights */}
                {crossModuleSummary.ai_insights?.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">AI Insights</h4>
                      <InsightsExport 
                        insights={crossModuleSummary.ai_insights.map(i => ({
                          title: i.type?.charAt(0).toUpperCase() + i.type?.slice(1) + ' Alert',
                          description: i.message,
                          severity: i.type === 'critical' ? 'critical' : i.type === 'warning' ? 'warning' : 'positive',
                          category: 'Cross-Module Intelligence'
                        }))} 
                        pageTitle="AI Intelligence" 
                        size="sm"
                      />
                    </div>
                    {crossModuleSummary.ai_insights.map((insight, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border-l-4 ${
                          insight.type === 'critical' ? 'bg-red-50 border-red-500' :
                          insight.type === 'warning' ? 'bg-amber-50 border-amber-500' :
                          insight.type === 'positive' ? 'bg-emerald-50 border-emerald-500' :
                          'bg-slate-50 border-slate-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {insight.type === 'critical' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                          {insight.type === 'warning' && <AlertCircle className="h-4 w-4 text-amber-500" />}
                          {insight.type === 'positive' && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                          <span className="text-sm">{insight.message}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Predictions */}
                {crossModuleSummary.predictions?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">4-Week Predictions</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {crossModuleSummary.predictions.map((pred, idx) => (
                        <div key={idx} className="p-3 bg-[#00b0ab]/10 dark:bg-[#00b0ab]/20 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{pred.metric}</span>
                            <Badge variant="outline">{pred.confidence}% confidence</Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-muted-foreground">{typeof pred.current === 'number' ? pred.current.toFixed(1) : pred.current}</span>
                            <ArrowUpRight className={`h-4 w-4 ${pred.projected > pred.current ? 'text-emerald-500' : 'text-red-500'}`} />
                            <span className="font-bold">{typeof pred.projected === 'number' ? pred.projected.toFixed(1) : pred.projected}</span>
                          </div>
                          {pred.recommendation && (
                            <p className="text-xs text-muted-foreground mt-1">{pred.recommendation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Sources */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <Database className="h-3 w-3" />
                    Data Sources: {crossModuleSummary.data_consistency?.all_sources_available ? 
                      <span className="text-emerald-600">All primary sources connected</span> :
                      <span className="text-amber-600">Some sources unavailable</span>
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Financial Intelligence Section */}
          {financialInsights?.insights?.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                  Financial Intelligence
                </CardTitle>
                <CardDescription>Key financial metrics and AI insights for {financialInsights.month}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {financialInsights.insights.map((insight, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border-l-4 ${
                        insight.type === 'critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                        insight.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500' :
                        insight.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500' :
                        'bg-slate-50 dark:bg-slate-800/50 border-slate-400'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{insight.title}</span>
                        {insight.type === 'critical' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                        {insight.type === 'warning' && <AlertCircle className="h-4 w-4 text-amber-500" />}
                        {insight.type === 'success' && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      </div>
                      <p className={`text-xl font-bold ${
                        insight.type === 'critical' ? 'text-red-600' :
                        insight.type === 'warning' ? 'text-amber-600' :
                        insight.type === 'success' ? 'text-emerald-600' :
                        'text-slate-700 dark:text-slate-300'
                      }`}>
                        {insight.metric}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{insight.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* KPI Analysis Tab */}
        <TabsContent value="kpis" className="space-y-4">
          {weeklyAnalysis?.categories && Object.entries(weeklyAnalysis.categories).map(([key, category]) => (
            <Card key={key}>
              <CardHeader>
                <CardTitle>{category.name}</CardTitle>
                <CardDescription>Data source: {category.data_source}</CardDescription>
              </CardHeader>
              <CardContent>
                {category.kpis?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">KPI</th>
                          <th className="text-center p-2">Current</th>
                          <th className="text-center p-2">Previous</th>
                          <th className="text-center p-2">Change</th>
                          <th className="text-center p-2">4-Wk Avg</th>
                          <th className="text-center p-2">Target</th>
                          <th className="text-center p-2">Trend</th>
                          <th className="text-center p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.kpis.map((kpi, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-medium">{kpi.name}</td>
                            <td className="text-center p-2">{kpi.current_value ?? '-'}</td>
                            <td className="text-center p-2">{kpi.previous_value ?? '-'}</td>
                            <td className="text-center p-2">
                              <span className={kpi.pct_change > 0 ? 'text-emerald-600' : kpi.pct_change < 0 ? 'text-red-600' : ''}>
                                {kpi.pct_change > 0 ? '+' : ''}{kpi.pct_change}%
                              </span>
                            </td>
                            <td className="text-center p-2">{kpi.ma_4week}</td>
                            <td className="text-center p-2">{kpi.target}</td>
                            <td className="text-center p-2">{TREND_ICONS[kpi.trend_8week]}</td>
                            <td className="text-center p-2">
                              <Badge className={`${RISK_COLORS[kpi.risk_classification]?.bg} ${RISK_COLORS[kpi.risk_classification]?.text}`}>
                                {kpi.risk_classification}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#00b0ab]" />
                4-Week KPI Forecasts
              </CardTitle>
              <CardDescription>Predictive analytics based on historical trends</CardDescription>
            </CardHeader>
            <CardContent>
              {predictions?.categories && Object.entries(predictions.categories).map(([key, kpis]) => (
                <div key={key} className="mb-6">
                  <h3 className="font-semibold text-lg mb-3 capitalize">{key}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {kpis?.map((forecast, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{forecast.name}</h4>
                          <Badge variant={forecast.trajectory === 'IMPROVING' ? 'default' : forecast.trajectory === 'DECLINING' ? 'destructive' : 'secondary'}>
                            {forecast.trajectory}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Current:</span>
                            <span className="ml-2 font-medium">{forecast.current_value}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Target:</span>
                            <span className="ml-2">{forecast.target}</span>
                          </div>
                        </div>
                        {forecast.forecasts && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Forecast:</p>
                            <div className="flex justify-between text-sm">
                              <span>W1: {forecast.forecasts.week_1}</span>
                              <span>W2: {forecast.forecasts.week_2}</span>
                              <span>W3: {forecast.forecasts.week_3}</span>
                              <span>W4: {forecast.forecasts.week_4}</span>
                            </div>
                          </div>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            Miss probability: <span className={forecast.probability_of_missing_target > 50 ? 'text-red-600 font-medium' : ''}>{forecast.probability_of_missing_target}%</span>
                          </span>
                          <span className="text-xs text-muted-foreground">Confidence: {forecast.confidence}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Intelligence Tab */}
        <TabsContent value="risks" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-red-500" />
                  Risk Detections
                </CardTitle>
              </CardHeader>
              <CardContent>
                {riskIntel?.risk_detections?.length > 0 ? (
                  <div className="space-y-3">
                    {riskIntel.risk_detections.map((risk, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        risk.severity === 'HIGH' ? 'bg-red-50 border-red-200' :
                        risk.severity === 'MEDIUM' ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50 border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{risk.type}</span>
                          <Badge variant={risk.severity === 'HIGH' ? 'destructive' : 'secondary'}>
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-sm mt-1">{risk.message}</p>
                        {risk.recommendation && (
                          <p className="text-xs text-muted-foreground mt-2">
                            <strong>Action:</strong> {risk.recommendation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No risks detected</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-amber-500" />
                  Operational Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {riskIntel?.operational_alerts?.length > 0 ? (
                  <div className="space-y-3">
                    {riskIntel.operational_alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          <span className="font-medium">{alert.type}</span>
                        </div>
                        <p className="text-sm mt-1">{alert.message}</p>
                        {alert.clinician && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Clinician: {alert.clinician} | Visits: {alert.visits}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No operational alerts</p>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  Positive Developments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {riskIntel?.positive_developments?.length > 0 ? (
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {riskIntel.positive_developments.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No positive developments to report</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Diagnostics Tab */}
        <TabsContent value="diagnostics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-slate-500" />
                Data Integrity Diagnostics
              </CardTitle>
              <CardDescription>Validate data pipeline and KPI update integrity</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={runDiagnostics} disabled={loadingDiagnostics} className="mb-4">
                {loadingDiagnostics ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cpu className="h-4 w-4 mr-2" />}
                Run Full Diagnostics
              </Button>

              {diagnostics && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <div className={`h-3 w-3 rounded-full ${
                      diagnostics.overall_status === 'PASS' ? 'bg-emerald-500' :
                      diagnostics.overall_status === 'WARNING' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                    <span className="font-medium">Overall Status: {diagnostics.overall_status}</span>
                    <span className="text-muted-foreground text-sm ml-auto">
                      {diagnostics.summary?.passed}/{diagnostics.summary?.total_checks} checks passed
                    </span>
                  </div>

                  {Object.entries(diagnostics.collections || {}).map(([name, coll]) => (
                    <div key={name} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{name}</h4>
                        <Badge variant={coll.status === 'PASS' ? 'default' : coll.status === 'WARNING' ? 'secondary' : 'destructive'}>
                          {coll.status}
                        </Badge>
                      </div>
                      {coll.issues?.length > 0 && (
                        <ul className="text-sm text-amber-600 mt-2">
                          {coll.issues.map((issue, idx) => (
                            <li key={idx}>- {issue}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {diagnostics.fix_sequence?.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium mb-2">Recommended Fix Sequence:</h4>
                      <ol className="text-sm space-y-1">
                        {diagnostics.fix_sequence.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Analysis Tab */}
        <TabsContent value="ai" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-[#00b0ab]" />
                    AI-Powered Analysis
                  </CardTitle>
                  <CardDescription>GPT-4o powered executive intelligence</CardDescription>
                </div>
                <AIExportDropdown token={token} reportType="insights" />
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={generateAIAnalysis} disabled={loadingAI} className="mb-4">
                {loadingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                Generate AI Analysis
              </Button>

              {aiAnalysis && (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{aiAnalysis.analysis}</ReactMarkdown>
                  <p className="text-xs text-muted-foreground mt-4">
                    Generated: {new Date(aiAnalysis.generated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {executiveBrief && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#00b0ab]" />
                  Executive Brief
                </CardTitle>
                <CardDescription>{executiveBrief.report_period}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {executiveBrief.sections?.kpi_performance && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2">KPI Performance</h4>
                      <div className="grid grid-cols-4 gap-2 text-center text-sm">
                        <div className="p-2 bg-emerald-100 rounded">
                          <p className="font-bold text-emerald-700">{executiveBrief.sections.kpi_performance.stable}</p>
                          <p className="text-xs">Stable</p>
                        </div>
                        <div className="p-2 bg-amber-100 rounded">
                          <p className="font-bold text-amber-700">{executiveBrief.sections.kpi_performance.warning}</p>
                          <p className="text-xs">Warning</p>
                        </div>
                        <div className="p-2 bg-orange-100 rounded">
                          <p className="font-bold text-orange-700">{executiveBrief.sections.kpi_performance.high_risk}</p>
                          <p className="text-xs">High Risk</p>
                        </div>
                        <div className="p-2 bg-red-100 rounded">
                          <p className="font-bold text-red-700">{executiveBrief.sections.kpi_performance.critical}</p>
                          <p className="text-xs">Critical</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {executiveBrief.recommended_actions?.length > 0 && (
                    <div className="p-4 bg-[#00b0ab]/10 rounded-lg">
                      <h4 className="font-medium mb-2">Recommended Actions</h4>
                      <ul className="text-sm space-y-1">
                        {executiveBrief.recommended_actions.map((action, idx) => (
                          <li key={idx}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#00b0ab]" />
                Scheduled AI Intelligence Reports
              </CardTitle>
              <CardDescription>
                Configure automatic executive briefs sent via email (daily, weekly, or monthly)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                <div>
                  <Label className="text-base font-medium">Enable Scheduled Reports</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically send AI Intelligence executive briefs
                  </p>
                </div>
                <Switch
                  checked={scheduleConfig.enabled}
                  onCheckedChange={(checked) => setScheduleConfig({...scheduleConfig, enabled: checked})}
                  data-testid="schedule-toggle"
                />
              </div>

              {/* Schedule Configuration */}
              <div className={`space-y-4 ${!scheduleConfig.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* Frequency Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Frequency</Label>
                    <Select 
                      value={scheduleConfig.frequency || 'weekly'} 
                      onValueChange={(v) => setScheduleConfig({...scheduleConfig, frequency: v})}
                    >
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(freq => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label} - {freq.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Day of Week - shown only for weekly */}
                  {scheduleConfig.frequency === 'weekly' && (
                    <div>
                      <Label>Day of Week</Label>
                      <Select 
                        value={scheduleConfig.day_of_week} 
                        onValueChange={(v) => setScheduleConfig({...scheduleConfig, day_of_week: v})}
                      >
                        <SelectTrigger data-testid="day-of-week-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map(day => (
                            <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Day of Month - shown only for monthly */}
                  {scheduleConfig.frequency === 'monthly' && (
                    <div>
                      <Label>Day of Month</Label>
                      <Select 
                        value={String(scheduleConfig.day_of_month || 1)} 
                        onValueChange={(v) => setScheduleConfig({...scheduleConfig, day_of_month: parseInt(v)})}
                      >
                        <SelectTrigger data-testid="day-of-month-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_MONTH.map(day => (
                            <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {/* Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Hour (24h)</Label>
                    <Select 
                      value={String(scheduleConfig.hour)} 
                      onValueChange={(v) => setScheduleConfig({...scheduleConfig, hour: parseInt(v)})}
                    >
                      <SelectTrigger data-testid="hour-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 24}, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Minute</Label>
                    <Select 
                      value={String(scheduleConfig.minute)} 
                      onValueChange={(v) => setScheduleConfig({...scheduleConfig, minute: parseInt(v)})}
                    >
                      <SelectTrigger data-testid="minute-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map(m => (
                          <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Recipients (comma-separated emails)</Label>
                  <Input
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    placeholder="admin@example.com, manager@example.com"
                    className="mt-1"
                    data-testid="recipients-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to send to all admins
                  </p>
                </div>
              </div>

              {/* Schedule Info */}
              {scheduleConfig.next_run && (
                <div className="p-3 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                  <p className="text-sm">
                    <strong>Next scheduled report:</strong>{' '}
                    {new Date(scheduleConfig.next_run).toLocaleString()}
                    <Badge variant="secondary" className="ml-2">{scheduleConfig.frequency || 'weekly'}</Badge>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button onClick={saveScheduleConfig} disabled={savingSchedule} data-testid="save-schedule-btn">
                  {savingSchedule ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings className="h-4 w-4 mr-2" />}
                  Save Schedule
                </Button>
                <Button variant="outline" onClick={runReportNow} data-testid="send-now-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Send Report Now
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Report History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-slate-500" />
                Report History
              </CardTitle>
              <CardDescription>Recently sent AI Intelligence reports</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-4">
                Report history will appear here after reports are sent
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
